import {
  Box,
  Card,
  Checkbox,
  defaultTheme,
  LazyImage,
  Link,
} from "@bca/ui-library";
import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { BorderProps, border } from "styled-system";

import PopoverMobile from "../print/PopoverMobile";
import { VehicleResultCardProps } from "./VehicleResultCard.type";
import getCMSDomain from "./getCMSDomain";
import getEnvironment from "../getEnvironment";
import getBackgroundColor from "./getBackgroundColor";
import { IVehicleDetailsModule } from "../common/analytics/analytics.types";
import analytics from "../common/analytics/analytics";
import { IProductPushable } from "../common/analytics/analytics.types";
import ScrollTo from "../utils/ScrollTo";
import LinkToDetailsPage, {
  itemCleanedLink,
} from "./components/LinkToDetailsPage";
import VehicleNotes from "./components/VehicleNotes";
import VehicleLabels from "./components/VehicleLabels";

const PositionedBox = styled(Box)`
  position: absolute;
  z-index: 1;
  bottom: 12px;
`;

const GradientBox = styled(Box)`
  background-image: linear-gradient(transparent, transparent, #070b16);
  z-index: 1;
`;

const ShadowedCard = styled(Card)<BorderProps>`
  ${border}

  box-shadow: ${defaultTheme.shadows.default};
  :hover {
    box-shadow: ${defaultTheme.shadows.hover};
  }
  margin-left: 0;
  overflow: visible;
`;

const GreyBox = styled(Box)`
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const StyledLink = styled(Link)`
  display: inline-block;
  margin: 12px 0px;
  font-size: 18px;
  font-weight: 500;
`;

const pushSelectAnalytics = (
  vehicleAnalytics: IVehicleDetailsModule,
  action: "select" | "unselect"
) => {
  analytics.push<IProductPushable>({
    event: {
      name: "click",
      category: "button",
      label: "vehicleSelect",
      action,
    },
    product: {
      vehicle: [vehicleAnalytics],
    },
  });
};

const VehicleResultCardMobile: React.FunctionComponent<
  VehicleResultCardProps
> = ({
  images,
  exportId,
  vrmNumber,
  lotNumber,
  primaryDescription,
  keyDecisionInfoSlot,
  badgesSlot,
  saleInfoSlot,
  secondaryInfoSlot,
  trackBtnSlot,
  pricingInfoSlot,
  printUrls,
  userNote,
  isTracked,
  vehicleAnalytics,
  selectedVehicles,
  pdfEnabled,
  setSelectedVehicles,
  viewportWidth,
}) => {
  const [backgroundColor, setBackgroundColor] = useState("");
  const CMSdomainURL = getCMSDomain(getEnvironment());

  const getSelectedStatus = useCallback(() => {
    return Boolean(selectedVehicles.find((vrm) => vrm === exportId));
  }, [selectedVehicles, exportId]);

  const addToSelection = () => {
    setSelectedVehicles([...selectedVehicles, exportId]);
  };

  const removeFromSelection = () => {
    setSelectedVehicles((selectedVehicles) => {
      return selectedVehicles?.filter((vrm) => vrm !== exportId);
    });
  };

  useEffect(() => {
    setBackgroundColor(getBackgroundColor(getSelectedStatus(), isTracked));
  }, [isTracked, getSelectedStatus]);

  let queryParamCardItem: string = "";
  let deepLinkScrollFinished: boolean = false;

  if (!deepLinkScrollFinished) {
    const urlParams = new URLSearchParams(window.location.search);
    queryParamCardItem = urlParams?.get("vrm");
  }

  const handleScrollEnd = () => {
    deepLinkScrollFinished = true;
  };

  const getIsQueryVrmMatch = (vrm: string) => {
    return vrm === queryParamCardItem;
  };

  const getIsDeepLinking = (vrm: string) => {
    return !deepLinkScrollFinished && getIsQueryVrmMatch(vrm);
  };

  function removeWhiteSpace(vrm: string) {
    return vrm.replace(/\s+/g, "");
  }

  return (
    <ScrollTo
      id={removeWhiteSpace(vrmNumber)}
      behavior="smooth"
      onScrollEnd={handleScrollEnd}
      active={getIsDeepLinking(removeWhiteSpace(vrmNumber))}
      key={vrmNumber}
    >
      <ShadowedCard
        width="100%"
        height="100%"
        paddingX="0"
        paddingY="0"
        marginY={0}
        border={
          getSelectedStatus() &&
          `1px solid ${defaultTheme.colors["msg-blue-border"]}`
        }
        css={
          getSelectedStatus() &&
          `
          :hover {
            box-shadow: 0px 3px 25px ${defaultTheme.colors["msg-blue-border"]};
          }
        `
        }
      >
        <LinkToDetailsPage
          CMSdomainURL={CMSdomainURL}
          vrmNumber={vrmNumber}
          dataTestId="card-link-mobile"
        />
        <Box width="auto" height="100%" display="flex" flexDirection="column">
          <Box
            width="auto"
            height="190px"
            css={`
              z-index: 0;
              position: relative;
            `}
          >
            <LinkToDetailsPage
              CMSdomainURL={CMSdomainURL}
              vrmNumber={vrmNumber}
              dataTestId="card-link-mobile"
            />
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
            >
              <Box width="auto" height="auto" margin="10px 5px 3px 8px">
                {badgesSlot}
              </Box>
              <Box
                p={0}
                onClick={(event) => {
                  event.stopPropagation();
                }}
                alignSelf="center"
                css={`
                  z-index: 20;
                `}
              >
                <Checkbox
                  id="result-card-checkbox"
                  checked={getSelectedStatus()}
                  onChange={() => {
                    pushSelectAnalytics(
                      vehicleAnalytics,
                      getSelectedStatus() ? "unselect" : "select"
                    );
                    if (getSelectedStatus() === true) {
                      removeFromSelection();
                      return;
                    }
                    addToSelection();
                  }}
                  css={`
                    z-index: 20;
                  `}
                ></Checkbox>
              </Box>
            </Box>
            <PositionedBox
              width="100%"
              px={3}
              display="flex"
              flexDirection="row"
              flex-wrap="wrap"
              justifyContent="space-between"
            >
              <LinkToDetailsPage
                CMSdomainURL={CMSdomainURL}
                vrmNumber={vrmNumber}
                dataTestId="card-link-mobile"
              />
              <Box
                display="flex"
                flexDirection="row"
                maxWidth="60%"
                flexWrap="wrap"
                alignItems="flex-end"
              >
                <VehicleLabels
                  vrmNumber={vrmNumber}
                  lotNumber={lotNumber}
                  viewportWidth={viewportWidth}
                  dataTestId="lotNumberMobile"
                />
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                css={`
                  z-index: 2;
                `}
              >
                {trackBtnSlot}
                {printUrls && printUrls?.length > 0 && (
                  <PopoverMobile
                    printUrls={printUrls}
                    vehicleAnalytics={vehicleAnalytics}
                    pdfEnabled={pdfEnabled}
                  />
                )}
              </Box>
            </PositionedBox>
            <GradientBox
              width="100%"
              height="190px"
              css={`
                z-index: -1;
                position: absolute;
                top: 0;
                border-top-right-radius: 8px;
                border-top-left-radius: 8px;
              `}
            />
            <LazyImage
              src={images[0]?.url}
              alt={images[0]?.alt}
              width="100%"
              height="190px"
              css={`
                z-index: -2;
                position: absolute;
                top: 0;
                border-top-left-radius: 8px;
                border-top-right-radius: 8px;
              `}
            />
          </Box>
          <Box width="auto" padding="20px 20px 0 20px">
            <Box width="auto" height="auto">
              {saleInfoSlot}
            </Box>
            <StyledLink href={itemCleanedLink(CMSdomainURL, vrmNumber)}>
              {primaryDescription}
            </StyledLink>
            <Box width="auto" height="auto" marginY={0} mb={3}>
              {secondaryInfoSlot}
            </Box>
            <Box width="auto" height="auto" paddingBottom="20px">
              {keyDecisionInfoSlot}
            </Box>
          </Box>
          {Boolean(userNote) && <VehicleNotes userNote={userNote} />}
          <GreyBox width="auto" height="auto" backgroundColor={backgroundColor}>
            <Box>{pricingInfoSlot}</Box>
          </GreyBox>
        </Box>
      </ShadowedCard>
    </ScrollTo>
  );
};

export default VehicleResultCardMobile;
